import { SearchEngine } from './common'
import { pubmedGetCount } from '@/utils/pubmed-api'

const baseFieldSpecifiers = ['Affiliation', 'AD', 'Article Identifier',
  'AID', 'All Fields', 'ALL', 'Author', 'AU', 'Author Identifier', 'AUID',
  'Book', 'book', 'Comment Corrections', 'Author - corporate',
  'Author - first', 'Author - full', 'Author - last', 'Corporate Author', 'CN',
  'Create Date', 'CRDT', 'Completion Date', 'DCOM', 'Conflict of Interest',
  'Conflict of Interest Statements', 'COIS', 'Date - Completion',
  'Date - Create', 'Date - Entrez', 'Date - MeSH', 'Date - Modification',
  'Date - Publication', 'EC/RN Number', 'RN', 'Editor', 'ED', 'Entrez Date',
  'EDAT', 'Filter', 'First Author Name', '1AU', 'Full Author Name', 'FAU',
  'Full Investigator Name', 'FIR', 'Grant Number', 'GR', 'Investigator', 'IR',
  'Investigator - Full', 'ISBN', 'ISBN', 'Issue', 'IP', 'Journal', 'TA',
  'Language', 'LA', 'Last Author', 'LASTAU', 'Location ID', 'LID', 'MeSH Date',
  'MHDA', 'MeSH Major Topic', 'MAJR', 'MeSH Subheading', 'SH', 'Mesh',
  'MeSH Terms', 'MH', 'Modification Date', 'LR', 'NLM Unique ID', 'JID',
  'Other Term', 'OT', 'Owner', 'Pagination', 'PG', 'Personal Name as Subject',
  'PS', 'Pharmacological Action', 'PA', 'Place of Publication', 'PL', 'PMID',
  'Publisher', 'PUBN', 'Publication Date', 'DP', 'Publication Type', 'PT',
  'Secondary Source ID', 'SI', 'Subset', 'SB', 'Supplementary Concept', 'NM',
  'Subject - Personal Name', 'Text', 'Text Words', 'Text Word', 'TW', 'Title',
  'TI', 'Title/Abstract', 'TIAB', 'Transliterated Title', 'TT', 'UID', 'Volume',
  'VI', 'PDAT']
const extendedBaseFieldSpecifiers = [...baseFieldSpecifiers, ...baseFieldSpecifiers.map(x => { return `${x}:noexp` })]
const defaultFieldSpecifiers = extendedBaseFieldSpecifiers.map(x => {
  return { name: x }
})
const NLP_PARAMETERS = '&confidence=0.6&match=exact&endpoint=MESH&limit=100&window=8&format=simplified&keywords=regex&word_embeddings=true'
const andNotReplaceRE = new RegExp('AND NOT', 'g')
const orNotReplaceRE = new RegExp('OR NOT', 'g')

const defaultSyntaxGuideUrl = 'https://pubmed.ncbi.nlm.nih.gov/help/#author-search'

const cachedCounts = new Map()

class PubMedEngine extends SearchEngine {
  constructor (fieldSpecifiers) {
    super('PubMed')
    this.fieldSpecifiers = fieldSpecifiers || defaultFieldSpecifiers
    this.syntaxGuideUrl = defaultSyntaxGuideUrl
  }
  mapOperator (operator) {
    const operatorToUse = operator.startsWith('NEAR') || operator.startsWith('ADJ')
      ? 'AND'
      : operator
    return { op: operatorToUse, spaces: true }
  }
  formulateQuery (query) {
    return 'https://pubmed.ncbi.nlm.nih.gov/?term=' + encodeURIComponent(query)
  }
  formulateQueryFromNodeData (nodeData) {
    const baseTerm = nodeData.text.replace(SearchEngine.quoteReplaceRE, '\\"')
    return this.formTerm(baseTerm, nodeData.isNegative, this.addQuotes(nodeData),
      nodeData.fieldSpecifier)
  }
  formTerm (text, isNegative, addQuotes, fieldSpecifier) {
    let r = ''
    if (isNegative) {
      r += 'NOT '
    }
    if (addQuotes) {
      r += '"'
    }
    r += text
    if (addQuotes) {
      r += '"'
    }
    if (fieldSpecifier && fieldSpecifier.length > 0) {
      r += '[' + fieldSpecifier + ']'
    }
    return r
  }
  addQuotes (node) {
    return this.addQuotesAroundPhrases && node && node.text && node.text.indexOf(' ') > 0 && !node.text.endsWith('*')
  }
  lastPass (r) {
    return r.replace(andNotReplaceRE, 'NOT').replace(orNotReplaceRE, 'NOT')
  }
  lintQuery (queryGraphData) {
    const m = this.lintFieldSpecifiers(queryGraphData, this.fieldSpecifiers)
    const r = m ? [m] : []
    const proximityOperators = new Set()
    this.scanGraphQueryData(queryGraphData, partData => {
      if (partData.operator) {
        if (partData.operator &&
          (partData.operator.startsWith('NEAR') || partData.operator.startsWith('ADJ'))
        ) {
          proximityOperators.add(partData.operator)
        }
      }
    })
    if (proximityOperators.size > 0) {
      const stringList = Array.from(proximityOperators.values()).join(', ')
      r.push(this.formatErrorMessage('proximityOperator', stringList, 'https://pubmed.ncbi.nlm.nih.gov/help/'))
    }
    return r
  }
  canGetCount () {
    return true
  }
  getCount (terms) {
    const termsToUpdate = []
    const now = new Date()
    const r = []
    const expiredKeys = []
    cachedCounts.forEach((value, key) => {
      if (value.expiry <= now) {
        expiredKeys.push(key)
      }
    })
    expiredKeys.forEach(e => {
      cachedCounts.delete(e)
    })
    terms.forEach(t => {
      const c = cachedCounts.get(t.terms)
      if (c) {
        r.push({key: t.key, count: c.count})
      } else {
        termsToUpdate.push(t)
      }
    })
    return new Promise((resolve, reject) => {
      pubmedGetCount(termsToUpdate).then(newCounts => {
        if (newCounts) {
          let newExpiry = new Date()
          newExpiry.setDate(newExpiry.getDate() + 1)
          newCounts.forEach(nc => {
            if (typeof nc.count !== 'undefined' && nc.count !== null) {
              const keyCountPair = {key: nc.key, count: nc.count}
              cachedCounts.set(nc.terms, {...keyCountPair, expiry: newExpiry})
              r.push(keyCountPair)
            }
          })
        }
        resolve(r)
      }).catch(reason => {
        console.warn('call to pubmedGetCount failed', reason)
        reject(reason)
      })
    })
  }
  getSuggestTermsMetaData () {
    return this.getSuggestTermsMeshSettings()
  }
  nlpParameters () {
    return NLP_PARAMETERS
  }
}

export default PubMedEngine



// WEBPACK FOOTER //
// ./src/formulate_queries/PubMedEngine.js